// Overview: is this tenant healthy, what's in it, who runs it, and where it
// lives. Everything the old League Detail "Usage & engagement" section showed
// (content inventory, onboarding funnel, fan traffic) is here; the admin
// roster and login history live on People and Activity.

import { Link } from 'react-router-dom'
import { api } from '@/api'
import type { TenantDetail, TenantTab, TenantUsage } from '@/api/types'
import {
  AccountStatusPill,
  BarList,
  Card,
  CardHeader,
  CopyButton,
  ErrorState,
  ExternalLinkIcon,
  KeyValueList,
  LineChart,
  LivePill,
  OnboardingSteps,
  PlanPill,
  RelativeTime,
  SkeletonRows,
  StatTile,
  Badge,
} from '@/components/ui'
import { ADDONS, enabledAddons } from '@/lib/addons'
import { auditHeadline } from '@/lib/audit'
import { cn, daysUntil, formatCount, formatDate, titleCase } from '@/lib/format'
import { useQuery } from '@/lib/query'
import { tenantLinks } from '@/lib/tenant'
import { useTenant } from '../tenantContext'

const ROLE_LABELS = { admin: 'Admin', editor: 'Editor', 'stat-keeper': 'Stat keeper' } as const

interface Issue {
  tone: 'bad' | 'warn'
  text: string
  tab: TenantTab
}

/** What's wrong with this tenant right now, worst first. */
function healthIssues(t: TenantDetail, adminCount: number | null): Issue[] {
  const out: Issue[] = []
  const a = t.account
  if (a.status === 'suspended') out.push({ tone: 'bad', text: 'Suspended: the public site and league admin are locked.', tab: 'danger-zone' })
  if (a.status === 'canceled') out.push({ tone: 'bad', text: 'Canceled: the public site is offline.', tab: 'plan' })
  if (a.status === 'past_due') out.push({ tone: 'warn', text: 'Payment is past due. The site stays live for now.', tab: 'plan' })
  if (a.status === 'trialing') {
    const d = daysUntil(a.trial_ends_at)
    if (d !== null && d <= 0) out.push({ tone: 'bad', text: 'The trial has ended, so the public site is offline.', tab: 'plan' })
    else if (d !== null && d <= 7) out.push({ tone: 'warn', text: `The trial ends in ${d} day${d === 1 ? '' : 's'}.`, tab: 'plan' })
  }
  const ds = t.domains.custom_domain_state
  if (ds === 'error') out.push({ tone: 'bad', text: `${t.custom_domain} has a DNS or certificate error.`, tab: 'domains' })
  if (ds === 'pending') out.push({ tone: 'warn', text: `${t.custom_domain} is waiting on the customer's DNS.`, tab: 'domains' })
  if (ds === 'unregistered') out.push({ tone: 'warn', text: `${t.custom_domain} isn't registered with Cloudflare yet.`, tab: 'domains' })
  if (t.sports.length > 0 && !t.sports.some((s) => s.is_enabled)) out.push({ tone: 'bad', text: 'No sport pack is enabled, so the tenant admin has nothing to manage.', tab: 'sports' })
  if (adminCount === 0) out.push({ tone: 'warn', text: 'Nobody can sign in to run this league.', tab: 'people' })
  const idle = t.last_activity_at ? -(daysUntil(t.last_activity_at) ?? 0) : null
  if (idle !== null && idle >= 14 && a.status !== 'suspended' && a.status !== 'canceled') out.push({ tone: 'warn', text: `No league-admin activity in ${idle} days.`, tab: 'activity' })
  return out
}

export default function OverviewTab() {
  const { tenant } = useTenant()
  const usage = useQuery(['tenant', tenant.id, 'usage'], (signal) => api.tenants.usage(tenant.id, signal))
  const admins = useQuery(['tenant', tenant.id, 'admins'], (signal) => api.tenants.admins.list(tenant.id, signal))
  const recent = useQuery(['tenant', tenant.id, 'recent'], (signal) => api.audit.list({ league_id: tenant.id, limit: 5 }, signal))

  const adminCount = admins.data ? admins.data.admins.length : tenant.admin_count
  const issues = healthIssues(tenant, adminCount)
  const links = tenantLinks(tenant)
  const on = enabledAddons(tenant.addons)

  return (
    <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
      <div className="grid min-w-0 content-start gap-4">
        <Card>
          <CardHeader title="Health" />
          {issues.length === 0 ? (
            <p className="flex items-center gap-2 text-[13.5px] text-ink">
              <span aria-hidden className="h-2 w-2 rounded-full bg-good" />
              Nothing needs attention.
            </p>
          ) : (
            <ul className="grid gap-2">
              {issues.map((i) => (
                <li key={i.text} className="flex items-start gap-2.5 text-[13.5px]">
                  <span aria-hidden className={cn('mt-[7px] h-2 w-2 shrink-0 rounded-full', i.tone === 'bad' ? 'bg-bad' : 'bg-warn')} />
                  <span className="min-w-0 flex-1">
                    {i.text}{' '}
                    <Link to={`../${i.tab}`} className="whitespace-nowrap text-xs font-semibold text-accent hover:underline">
                      Fix
                    </Link>
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-4 border-t border-line pt-4">
            <p className="mb-2 text-2xs font-bold uppercase tracking-eyebrow text-muted">Setup progress</p>
            {usage.data ? (
              <OnboardingSteps state={usage.data.onboarding} />
            ) : usage.status === 'error' ? (
              <p className="text-xs text-muted">Couldn't load setup progress.</p>
            ) : (
              <div className="h-8 animate-pulse rounded bg-hover/60" role="status" aria-label="Loading setup progress" />
            )}
          </div>
        </Card>

        <ContentNumbers tenant={tenant} usage={usage.data} loading={usage.status === 'loading'} />

        <FanTraffic usage={usage.data} status={usage.status} error={usage.error} onRetry={usage.refetch} />

        <Card flush className="overflow-hidden">
          <div className="px-4 pt-4 sm:px-5">
            <CardHeader
              title="Recent changes"
              actions={
                <Link to="../activity" className="text-xs font-semibold text-accent hover:underline">
                  All activity
                </Link>
              }
            />
          </div>
          {recent.status === 'loading' ? (
            <SkeletonRows rows={3} label="Loading recent changes" />
          ) : recent.status === 'error' ? (
            <ErrorState error={recent.error} onRetry={recent.refetch} className="py-8" />
          ) : !recent.data?.entries.length ? (
            <p className="px-4 pb-4 text-[13px] text-muted sm:px-5">No changes recorded for this tenant yet.</p>
          ) : (
            <ul className="divide-y divide-line border-t border-line">
              {recent.data.entries.map((e) => (
                <li key={e.id} className="grid gap-0.5 px-4 py-2.5 sm:px-5">
                  <span className="min-w-0 break-words text-[13px] text-ink">{auditHeadline(e)}</span>
                  <span className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted">
                    <span className="min-w-0 break-all">{e.actor ? e.actor.name || e.actor.email : 'System'}</span>
                    {e.actor?.is_super_admin ? <Badge tone="outline">ScoreLM</Badge> : null}
                    <span aria-hidden>·</span>
                    <RelativeTime value={e.created_at} />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="grid min-w-0 content-start gap-4">
        <Card>
          <CardHeader title="Links" description={links.publicHost === tenant.custom_domain ? 'The custom domain is verified, so it is the public address.' : undefined} />
          <ul className="grid gap-3">
            {links.publicUrl ? <LinkRow label="Public site" url={links.publicUrl} primary /> : null}
            {links.adminUrl ? <LinkRow label="League admin" url={links.adminUrl} /> : null}
            {links.subdomainUrl && links.subdomainUrl !== links.publicUrl ? <LinkRow label="Subdomain" url={links.subdomainUrl} note={tenant.domains.redirect_to_custom ? 'redirects to the custom domain' : undefined} /> : null}
            {links.customUrl && links.customUrl !== links.publicUrl ? (
              <LinkRow label="Custom domain" url={links.customUrl} note={tenant.domains.custom_domain_state === 'active' ? undefined : 'not verified yet'} />
            ) : null}
          </ul>
        </Card>

        <Card>
          <CardHeader
            title="Plan"
            actions={
              <Link to="../plan" className="text-xs font-semibold text-accent hover:underline">
                Manage
              </Link>
            }
          />
          <KeyValueList
            items={[
              {
                label: 'Status',
                value: (
                  <span className="flex flex-wrap gap-1.5">
                    <AccountStatusPill account={tenant.account} />
                    {tenant.account.status !== 'suspended' ? <LivePill isLive={tenant.account.is_live} /> : null}
                  </span>
                ),
              },
              { label: 'Package', value: <PlanPill account={tenant.account} /> },
              {
                label: tenant.account.status === 'trialing' ? 'Trial ends' : 'Renews',
                value: formatDate(tenant.account.status === 'trialing' ? tenant.account.trial_ends_at : tenant.account.current_period_end),
              },
              {
                label: 'Add-ons',
                value: on.length ? on.map((k) => ADDONS[k].label).join(', ') : <span className="text-muted">None</span>,
              },
              { label: 'Default sport', value: titleCase(tenant.default_sport_id) },
              ...(tenant.site_template ? [{ label: 'Website template', value: tenant.site_template.name }] : []),
              { label: 'Created', value: formatDate(tenant.created_at) },
              { label: 'Tenant id', value: <span className="font-mono">{tenant.id}</span> },
            ]}
          />
        </Card>

        <Card>
          <CardHeader
            title="League admins"
            actions={
              <Link to="../people" className="text-xs font-semibold text-accent hover:underline">
                Manage
              </Link>
            }
          />
          {admins.status === 'loading' ? (
            <SkeletonRows rows={2} label="Loading league admins" className="-mx-4 sm:-mx-5" />
          ) : admins.status === 'error' ? (
            <p className="text-[13px] text-muted">Couldn't load the admins.</p>
          ) : !admins.data?.admins.length ? (
            <p className="text-[13px] text-muted">No league admins yet. Add one on the People tab.</p>
          ) : (
            <ul className="grid gap-2.5">
              {admins.data.admins.slice(0, 5).map((a) => (
                <li key={a.user_id} className="flex min-w-0 items-start justify-between gap-3 text-[13px]">
                  <span className="grid min-w-0 gap-0.5">
                    <span className="truncate font-semibold text-ink">{a.name || a.email}</span>
                    {a.name ? <span className="truncate text-xs text-muted">{a.email}</span> : null}
                  </span>
                  <span className="grid shrink-0 justify-items-end gap-0.5 text-xs">
                    <span className="text-muted">{ROLE_LABELS[a.role]}</span>
                    <RelativeTime value={a.last_login_at} staleAfterDays={30} fallback="Never signed in" className="text-subtle" />
                  </span>
                </li>
              ))}
              {admins.data.admins.length > 5 ? <li className="text-xs text-muted">and {admins.data.admins.length - 5} more</li> : null}
            </ul>
          )}
        </Card>
      </div>
    </div>
  )
}

function LinkRow({ label, url, note, primary }: { label: string; url: string; note?: string; primary?: boolean }) {
  return (
    <li className="grid gap-1">
      <span className="text-xs text-muted">
        {label}
        {note ? <span className="text-subtle"> · {note}</span> : null}
      </span>
      <span className="flex min-w-0 items-center gap-2">
        <a href={url} target="_blank" rel="noreferrer" className={cn('min-w-0 flex-1 break-all font-mono text-[12.5px] hover:underline', primary ? 'text-accent' : 'text-ink')}>
          {url.replace(/^https?:\/\//, '')}
          <ExternalLinkIcon size={12} className="ml-1 inline-block align-[-1px] text-subtle" />
        </a>
        <CopyButton value={url} ariaLabel={`Copy ${label} URL`} variant="ghost" />
      </span>
    </li>
  )
}

function ContentNumbers({ tenant, usage, loading }: { tenant: TenantDetail; usage: TenantUsage | undefined; loading: boolean }) {
  const inv = usage?.content_inventory
  const counts = inv ?? (tenant.counts ? { ...tenant.counts, tournaments: null } : null)
  const tiles: Array<[string, number | null | undefined]> = [
    ['Seasons', counts?.seasons],
    ['Teams', counts?.teams],
    ['Players', counts?.players],
    ['Games', counts?.games],
    ['Tournaments', counts?.tournaments],
  ]
  return (
    <section aria-label="Content" className="grid grid-cols-2 gap-3 sm:grid-cols-5">
      {tiles.map(([label, n]) => (
        <StatTile key={label} label={label} value={n === undefined || n === null ? (loading ? '…' : '–') : formatCount(n)} />
      ))}
    </section>
  )
}

function FanTraffic({ usage, status, error, onRetry }: { usage: TenantUsage | undefined; status: string; error: unknown; onRetry: () => void }) {
  const ft = usage?.fan_traffic
  return (
    <Card>
      <CardHeader title="Fan traffic" description="Public-site page views." />
      {status === 'loading' && !ft ? (
        <div className="h-48 animate-pulse rounded bg-hover/50" role="status" aria-label="Loading fan traffic" />
      ) : status === 'error' && !ft ? (
        <ErrorState error={error} onRetry={onRetry} className="py-8" />
      ) : ft ? (
        <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_14rem]">
          <div className="grid min-w-0 gap-3">
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <p>
                <span className="block text-xs text-muted">Last 7 days</span>
                <span className="font-display text-2xl font-extrabold tabular text-ink">{formatCount(ft.page_views_7d)}</span>
              </p>
              <p>
                <span className="block text-xs text-muted">Last 30 days</span>
                <span className="font-display text-2xl font-extrabold tabular text-ink">{formatCount(ft.page_views_30d)}</span>
              </p>
            </div>
            <LineChart data={ft.over_time} label="Daily page views" />
          </div>
          <div className="min-w-0">
            <p className="mb-2 text-2xs font-bold uppercase tracking-eyebrow text-muted">By page</p>
            <BarList
              label="Page views by page type"
              valueLabel="Views"
              items={[...ft.by_page_type].sort((a, b) => b.views - a.views).map((p) => ({ label: titleCase(p.page_type), value: p.views }))}
              emptyText="No page views yet."
            />
          </div>
        </div>
      ) : null}
    </Card>
  )
}
